import { FC, FocusEvent } from 'react';

import { getFormattedIntNumber } from 'utils/clickme';
import { SPACE } from 'utils/constants/common';

import cpcFlexibleSalaryFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_salary_form_field_touch';
import { FieldsGroupHelper, HSpacingContainer, Input } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { setCampaignErrors, setCampaignSalary } from 'store/models/createCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    placeholder: {
        from: 'AdCreate.AdForm.AdSalary.from',
        to: 'AdCreate.AdForm.AdSalary.to',
    },
    prefix: {
        from: 'AdCreate.AdForm.AdSalary.prefix.from',
        to: 'AdCreate.AdForm.AdSalary.prefix.to',
    },
    errorBothEmpty: 'AdCreate.AdForm.AdSalary.errorBothEmpty',
    errorFromGreaterThanTo: 'AdCreate.AdForm.AdSalary.errorFromGreaterThanTo',
    errorToLessThanFrom: 'AdCreate.AdForm.AdSalary.errorToLessThanFrom',
    currency: 'currency.short',
};

export const SalaryInputs: FC = () => {
    const { adSalary, campaignErrors } = useSelector((state) => state.createCampaign);
    const vacancyId = useSelector((state) => state.createBanner.bannerVacancyId);

    const { adSalaryMin, adSalaryMax, adSalaryCommon } = campaignErrors;

    const dispatch = useDispatch();
    const trl = useTrls();

    const validateSalaryInputs = (field: 'min' | 'max') => {
        if (!adSalary) return;

        const { min, max } = adSalary;

        if (min === null && max === null) {
            dispatch(
                setCampaignErrors({
                    ...campaignErrors,
                    adSalaryCommon: trl(TrlKeys.errorBothEmpty),
                    adSalaryMin: undefined,
                    adSalaryMax: undefined,
                })
            );
            return;
        }

        if (min !== null && max !== null) {
            if (field === 'min' && min > max) {
                dispatch(
                    setCampaignErrors({
                        ...campaignErrors,
                        adSalaryMin: trl(TrlKeys.errorFromGreaterThanTo),
                    })
                );
            }
            if (field === 'max' && max < min) {
                dispatch(
                    setCampaignErrors({
                        ...campaignErrors,
                        adSalaryMax: trl(TrlKeys.errorToLessThanFrom),
                    })
                );
            }
        }
    };

    const onChangeSalary = (value: string, field: 'min' | 'max') => {
        const numericValue = Number(value.replace(/[^0-9]/g, ''));
        if (field === 'min') {
            dispatch(setCampaignSalary({ min: numericValue || null, max: adSalary?.max ?? null }));
        } else {
            dispatch(setCampaignSalary({ min: adSalary?.min ?? null, max: numericValue || null }));
        }
    };

    const onFocus = () => {
        cpcFlexibleSalaryFormFieldTouch({ vacancyId });
        dispatch(
            setCampaignErrors({
                ...campaignErrors,
                adSalaryMin: undefined,
                adSalaryMax: undefined,
                adSalaryCommon: undefined,
            })
        );
    };

    return (
        <FieldsGroupHelper errorMessage={adSalaryCommon} invalid={!!adSalaryCommon}>
            <HSpacingContainer default={12}>
                <Input
                    value={
                        adSalary?.min ? `${trl(TrlKeys.prefix.from)}${SPACE}${getFormattedIntNumber(adSalary.min)}` : ''
                    }
                    onChange={(value) => onChangeSalary(value, 'min')}
                    onFocus={onFocus}
                    onBlur={() => validateSalaryInputs('min')}
                    placeholder={trl(TrlKeys.placeholder.from)}
                    invalid={!!adSalaryMin && !adSalaryCommon}
                    errorMessage={adSalaryMin}
                    data-qa="salary-min-input"
                    postfix={trl(TrlKeys.currency)}
                />
                <Input
                    value={
                        adSalary?.max ? `${trl(TrlKeys.prefix.to)}${SPACE}${getFormattedIntNumber(adSalary.max)}` : ''
                    }
                    onChange={(value) => onChangeSalary(value, 'max')}
                    onFocus={onFocus}
                    onBlur={(event: FocusEvent<HTMLInputElement>) => {
                        const nextTarget = event.relatedTarget;
                        // не валидируем на фронте, если мы в фокусе "до" и переходим на следующий шаг
                        // чтобы избежать моргания ошибок на разных инпутах
                        if (nextTarget?.closest?.('[data-qa=next]')) {
                            return;
                        }
                        validateSalaryInputs('max');
                    }}
                    placeholder={trl(TrlKeys.placeholder.to)}
                    invalid={!!adSalaryMax && !adSalaryCommon}
                    errorMessage={adSalaryMax}
                    data-qa="salary-max-input"
                    postfix={trl(TrlKeys.currency)}
                />
            </HSpacingContainer>
        </FieldsGroupHelper>
    );
};
